import { getProtectedAxiosInstance } from '@/services/axios/Axios';
import { formatPhotoTime } from '@/components/runs/views/classic-view/run-photo-check-camera.component';
import PhotoCloseButton from '@/components/runs/views/classic-view/photo-close-button.component';
import PhotoGeofenceBadge from '@/components/runs/views/classic-view/photo-geofence-badge.component';
import { Dialog } from '@headlessui/react';
import { notDoneLabel } from '@/services/photo-check/problem-label';
import { PhotoCheckItem } from '@/stores/run/run-photos.store';
import { useEffect, useState } from 'react';

/** The label for one task's result: not done says what is wrong ("Not empty", "Not clean") */
export function resultLabel(task: PhotoCheckItem['tasks'][number]) {
  if (task.suggestion === 'attention') {
    return {
      text: notDoneLabel(task.problem, task.title).label,
      className:
        task.problem === 'not_visible'
          ? SUGGESTION_LABEL.attention.className
          : 'bg-red-50 text-red-800'
    };
  }
  return SUGGESTION_LABEL[task.suggestion];
}

export const SUGGESTION_LABEL = {
  done: { text: 'Seen done', className: 'bg-green-50 text-green-800' },
  attention: { text: 'Not seen', className: 'bg-gray-100 text-gray-700' },
  review: { text: 'Unsure', className: 'bg-amber-50 text-amber-800' }
};

/**
 * Analytics > Photos: every photo taken in this checklist's runs, newest first, with when it was taken,
 * who took it, and what each task looked like in it. Links to the photos last 15 minutes; reopening the tab renews them.
 */
export default function PhotoChecksGalleryComponent({
  checklistId
}: {
  checklistId: string;
}) {
  const [items, setItems] = useState<PhotoCheckItem[]>();
  const [error, setError] = useState('');
  const [open, setOpen] = useState<PhotoCheckItem>();

  useEffect(() => {
    getProtectedAxiosInstance()
      .post('/run/photo-check/list', { checklist_id: checklistId })
      .then(r => setItems(r.data.data.items))
      .catch(() => setError('Could not load the photos. Please try again.'));
  }, [checklistId]);

  if (error) {
    return (
      <div className="rounded bg-red-50 text-red-800 text-sm p-3">{error}</div>
    );
  }
  if (!items) {
    return (
      <div className="flex justify-center p-10">
        <span className="inline-block h-8 w-8 rounded-full border-4 border-black/10 border-t-black/60 animate-spin" />
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="text-sm text-black/60 p-10 text-center">
        No photos yet. Photos taken with the camera on a task in a run show up
        here.
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map(item => {
          const done = item.tasks.filter(t => t.suggestion === 'done').length;
          const time = new Date(item.taken_at || item.checked_at);
          // A photo from one task's camera: show that task and its result
          const single = item.tasks.length === 1 ? item.tasks[0] : undefined;
          const label = single ? resultLabel(single) : undefined;
          return (
            <button
              key={item.id}
              type="button"
              className="text-left rounded-lg border border-black/10 overflow-hidden hover:shadow"
              onClick={() => setOpen(item)}
            >
              {item.photo ? (
                <img
                  src={item.photo}
                  alt={`Photo from ${item.run_name || 'a run'}`}
                  className="w-full h-40 object-cover bg-black/5"
                />
              ) : (
                <div className="w-full h-40 bg-black/5 flex items-center justify-center text-xs text-black/40">
                  Photo not stored
                </div>
              )}
              <div className="p-2 flex flex-col gap-0.5">
                <div className="flex items-start justify-between gap-1">
                  <span className="text-xs font-medium truncate">
                    {single ? single.title || 'Task' : item.run_name || 'Run'}
                  </span>
                  {label && (
                    <span
                      className={
                        'shrink-0 rounded px-1.5 py-0.5 text-[10px] ' +
                        label.className
                      }
                    >
                      {label.text}
                    </span>
                  )}
                </div>
                {single && (
                  <div className="text-xs text-black/60 truncate">
                    {item.run_name || 'Run'}
                  </div>
                )}
                <div className="text-xs text-black/60">
                  {formatPhotoTime(time)}
                </div>
                <div className="text-xs text-black/60 truncate">
                  {item.taken_by || 'Guest'}
                  {single ? '' : ` · ${done} of ${item.tasks.length} seen done`}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {open && (
        <PhotoCheckDetailDialog
          item={open}
          onClose={() => setOpen(undefined)}
        />
      )}
    </>
  );
}

/** One photo, large, with when it was taken and received, who took it, and each task's result */
export function PhotoCheckDetailDialog({
  item,
  onClose
}: {
  item: PhotoCheckItem;
  onClose: () => void;
}) {
  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-5xl rounded-lg bg-white p-4 shadow-xl">
            <div className="flex items-center justify-between gap-2">
              <Dialog.Title className="font-medium">
                {item.run_name || 'Run'}
              </Dialog.Title>
              <PhotoCloseButton onClick={() => onClose()} />
            </div>
            <div className="mt-3 flex flex-col md:flex-row gap-4">
              <div className="md:w-3/5 bg-black/5 rounded p-2 flex items-center justify-center">
                {item.photo ? (
                  <img
                    src={item.photo}
                    alt={`Photo from ${item.run_name || 'a run'}`}
                    className="max-h-[70vh] w-auto max-w-full rounded"
                  />
                ) : (
                  <span className="text-xs text-black/40">
                    Photo not stored
                  </span>
                )}
              </div>

              {/* Right: the result for each task, then the photo's details */}
              <div className="md:w-2/5 flex flex-col gap-4 overflow-y-auto max-h-[70vh]">
                <div className="flex flex-col gap-2">
                  {item.tasks.map((task, i) => {
                    const label = resultLabel(task);
                    return (
                      <div
                        key={i}
                        className="flex items-start justify-between gap-2 rounded border border-black/10 p-2 text-sm"
                      >
                        <span>{task.title || 'Task'}</span>
                        <span
                          className={
                            'shrink-0 rounded px-1.5 py-0.5 text-xs ' +
                            (label?.className || '')
                          }
                        >
                          {label?.text || task.suggestion}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                  <dt className="text-black/50">Taken</dt>
                  <dd className="m-0">
                    {formatPhotoTime(
                      new Date(item.taken_at || item.checked_at)
                    )}
                  </dd>
                  {item.taken_by && (
                    <>
                      <dt className="text-black/50">By</dt>
                      <dd className="m-0">{item.taken_by}</dd>
                    </>
                  )}
                  {item.taken_at && (
                    <>
                      <dt className="text-black/50">Received</dt>
                      <dd className="m-0">
                        {formatPhotoTime(new Date(item.checked_at))}
                      </dd>
                    </>
                  )}
                  <dt className="text-black/50">Location</dt>
                  <dd className="m-0">
                    <PhotoGeofenceBadge
                      geofence={item.geofence}
                      location={item.location}
                    />
                  </dd>
                </dl>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
